import React from 'react'
import { useTranslation } from 'react-i18next'
import logoLockup from '../../../assets/logo/logo-dark.svg'
import { COLORS, FONT, RADIUS, SHADOW } from './tokens'
import { SamaSymbol } from './icons'

// Shared light-grey page background (a near-flat off-white with a faint tint at
// the top). Used behind the content on BOTH breakpoints so the page background
// is the same grey on mobile and desktop — only the brand band stays viridian.
const PAGE_BACKGROUND = `radial-gradient(1100px 520px at 50% -8%, ${COLORS.viridian050} 0%, rgba(234,240,241,0) 62%), ${COLORS.pageWhite}`

interface Props {
    /**
     * Mobile: a full-bleed viridian brand band with the white content sheet
     * rising over it. Desktop: a centered, elevated card (viridian cap + white
     * body) on a softly tinted page — "open in app" has no desktop use, so the
     * single browser action gets a calm, premium single-purpose screen.
     */
    mobile: boolean
    /** Body content (heading, paragraph, action cards). */
    children: React.ReactNode
    /** Optional content shown below the actions (the app-download card). */
    bottomSlot?: React.ReactNode
    /** Optional trust line pinned at the very bottom (secure-sign-in note). */
    footer?: React.ReactNode
    /** Optional aria-live status node for screen-reader announcements. */
    statusNode?: React.ReactNode
}

/** Viridian header: the official Sama logo lockup + the symbol as a faint corner watermark. */
const BrandPanel: React.FC<{ minHeight: number; padding: string }> = ({
    minHeight,
    padding,
}) => {
    const { t } = useTranslation()
    return (
        <div
            style={{
                position: 'relative',
                overflow: 'hidden',
                background: COLORS.viridian,
                minHeight,
                padding,
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <SamaSymbol
                size={188}
                color={COLORS.ivory}
                style={{
                    position: 'absolute',
                    right: -44,
                    bottom: -52,
                    opacity: 0.07,
                    pointerEvents: 'none',
                }}
            />
            {/* logo-dark.svg is a single near-black path; recolour to ivory so
                the brand lockup (symbol + dot + wordmark) reads on the viridian band. */}
            <img
                src={logoLockup}
                alt={t('alt.logo')}
                style={{
                    height: 30,
                    width: 'auto',
                    display: 'block',
                    filter: 'brightness(0) invert(1)',
                }}
            />
        </div>
    )
}

export const AuthLayout: React.FC<Props> = ({
    mobile,
    children,
    bottomSlot,
    footer,
    statusNode,
}) => {
    const { i18n } = useTranslation()
    const language = i18n.resolvedLanguage

    // Content is grouped (actions → download → trust note) on both layouts
    // rather than pinned to the bottom — on tall phones a flex spacer left an
    // awkward mid-screen void. The download card and footer carry their own
    // top spacing/divider, so they read as one cohesive block.
    const body = (
        <>
            {children}
            {bottomSlot}
            {footer}
            {statusNode}
        </>
    )

    if (mobile) {
        return (
            <main
                lang={language}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100dvh',
                    padding: '24px 14px',
                    background: PAGE_BACKGROUND,
                    fontFamily: FONT.display,
                }}
            >
                {/* Same white card as desktop: ivory surface, hairline border and
                    soft shadow, fully rounded, floating on the grey page — so the
                    box reads as a distinct box on both breakpoints. */}
                <div
                    style={{
                        width: '100%',
                        maxWidth: 480,
                        display: 'flex',
                        flexDirection: 'column',
                        background: COLORS.ivory,
                        borderRadius: RADIUS.md,
                        overflow: 'hidden',
                        border: `1px solid ${COLORS.neutral200}`,
                        boxShadow: SHADOW.lg,
                    }}
                >
                    <BrandPanel minHeight={96} padding="30px 24px 30px" />
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '26px 18px 26px',
                        }}
                    >
                        {body}
                    </div>
                </div>
            </main>
        )
    }

    return (
        <main
            lang={language}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100dvh',
                padding: '40px 20px',
                fontFamily: FONT.display,
                background: PAGE_BACKGROUND,
            }}
        >
            <div
                style={{
                    width: 'min(460px, 100%)',
                    background: COLORS.ivory,
                    borderRadius: RADIUS.md,
                    overflow: 'hidden',
                    boxShadow: SHADOW.lg,
                    border: `1px solid ${COLORS.neutral200}`,
                }}
            >
                <BrandPanel minHeight={120} padding="30px 32px 34px" />
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '30px 32px 28px',
                    }}
                >
                    {body}
                </div>
            </div>
        </main>
    )
}
